import { useMemo } from "react";
import { Select } from "@/components/base/FormControls";
import type { TerritorySelection } from "@/config/territory";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import { cn } from "@/utils/cn";

interface TerritoryFiltersProps {
  value: TerritorySelection;
  onChange: (next: TerritorySelection) => void;
  /** Valeurs de secteur / quartier réellement présentes dans les données. */
  secteurOptions?: string[];
  idPrefix: string;
  className?: string;
}

/**
 * Filtres territoriaux en cascade : Région → Province → Commune / Ville →
 * Secteur / Quartier. Les listes proviennent du store territorial partagé
 * (source de vérité unique, éditable depuis le Super Admin), ce qui garantit
 * des choix toujours cohérents. Chaque sélection met à jour les niveaux suivants.
 */
export default function TerritoryFilters({
  value,
  onChange,
  secteurOptions = [],
  idPrefix,
  className,
}: TerritoryFiltersProps) {
  const { regionNames, getProvinceNames, getCommuneNames, getSecteurNames } = useTerritory();

  const regionOptions = useMemo(
    () => [
      { value: "", label: "Toutes les régions" },
      ...regionNames.map((name) => ({ value: name, label: name })),
    ],
    [regionNames]
  );

  const provinceOptions = useMemo(
    () => [
      { value: "", label: "Toutes les provinces" },
      ...getProvinceNames(value.region).map((name) => ({ value: name, label: name })),
    ],
    [getProvinceNames, value.region]
  );

  const communeOptions = useMemo(
    () => [
      { value: "", label: "Toutes les communes / villes" },
      ...getCommuneNames(value.region, value.province).map((name) => ({ value: name, label: name })),
    ],
    [getCommuneNames, value.region, value.province]
  );

  const effectiveSecteurs = useMemo(
    () => (secteurOptions.length > 0 ? secteurOptions : getSecteurNames(value.commune)),
    [secteurOptions, getSecteurNames, value.commune]
  );

  const secteurSelectOptions = useMemo(
    () => [
      { value: "", label: "Tous les secteurs / quartiers" },
      ...effectiveSecteurs.map((name) => ({ value: name, label: name })),
    ],
    [effectiveSecteurs]
  );

  const labelClass = "text-xs font-label font-medium text-foreground-700";

  return (
    <div className={cn("grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4", className)}>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-region`} className={labelClass}>
          Région
        </label>
        <Select
          id={`${idPrefix}-region`}
          value={value.region}
          onChange={(event) =>
            onChange({ region: event.target.value, province: "", commune: "", secteur: "" })
          }
          options={regionOptions}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-province`} className={labelClass}>
          Province
        </label>
        <Select
          id={`${idPrefix}-province`}
          value={value.province}
          onChange={(event) =>
            onChange({ ...value, province: event.target.value, commune: "", secteur: "" })
          }
          options={provinceOptions}
          disabled={!value.region}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-commune`} className={labelClass}>
          Commune / Ville
        </label>
        <Select
          id={`${idPrefix}-commune`}
          value={value.commune}
          onChange={(event) => onChange({ ...value, commune: event.target.value, secteur: "" })}
          options={communeOptions}
          disabled={!value.province}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-secteur`} className={labelClass}>
          Secteur / Quartier
        </label>
        <Select
          id={`${idPrefix}-secteur`}
          value={value.secteur}
          onChange={(event) => onChange({ ...value, secteur: event.target.value })}
          options={secteurSelectOptions}
          disabled={effectiveSecteurs.length === 0}
        />
      </div>
    </div>
  );
}